import {
  WallsAndRoofsDataCombined
} from './WallsAndRoofsDataCombined.js'
import {
  OLD_WallsAndRoofsDataCombined
} from './OLD_WallsAndRoofsDataCombined.js'

import {
  StrictParseNumber,
  IsNumberString,
  AssertThrow,
  CompareProjectVersions,
} from '../../SharedUtils.js'
import {makeEnumWithDataAndLabels} from '../../Base.js'

import {AbsorptanceData} from './Absorptance Data.js'

import { CsvHelper } from '../CsvHelper.js'
import Papa from 'papaparse';

function parseSimpleTable(rawCsv) {
  let csv = Papa.parse(rawCsv);
  let objs = [];
  let colNames = csv.data[1];
  for (let row = 2; row < csv.data.length; ++row) {
    let rowData = csv.data[row];
    let obj = {};
    for (let col = 0; col < rowData.length; ++col) {
      obj[colNames[col]] = rowData[col];
    }
    objs.push(obj);
  }
  return objs;
}

function IsValidString(str) {
  return typeof str === 'string' && str.length > 0;
}

export class WallMaterial {
  constructor(data) {
    this.id = data.Id;
    AssertThrow(IsValidString(this.id), "Material ID is invalid");
    this.name = data.Material;
    AssertThrow(IsValidString(this.name), "Material name is invalid");

    try {
      this.thickness = null;
      this.resistance = null;
      this.weight = null;
      this.conductivity = null;
      this.density = null;

      if (IsNumberString(data.Thickness)) {
        // The thickness, resistance, and weight are preset
        this.thickness = StrictParseNumber(data.Thickness);
        this.resistance = StrictParseNumber(data.Resistance);
        this.weight = StrictParseNumber(data.Weight);
        //AssertThrow(!IsNumberString(data.Conductivity), "Conductivity should not be set");
        //AssertThrow(!IsNumberString(data.Density), "Density should not be set");
      } else {
        // The thickness, resistance, and weight are user-defined
        this.thickness = null;
        this.conductivity = StrictParseNumber(data.Conductivity);
        this.density = StrictParseNumber(data.Density);
        //AssertThrow(!IsNumberString(data.Resistance), "Resistance should not be set");
        //AssertThrow(!IsNumberString(data.Weight), "Weight should not be set");
      }
    } catch (e) {
      console.error(`Error parsing WallMaterial ${this.name}, with data:`, data);
      throw e;
    }
  }

  requiresUserThickness() {
    return this.thickness === null;
  }

  getPresetThickness() {
    AssertThrow(this.thickness !== null, "Thickness is not preset");
    return this.thickness;
  }

  getPresetResistance() {
    AssertThrow(this.resistance !== null, "Resistance is not preset");
    return this.resistance;
  }

  getPresetWeight() {
    AssertThrow(this.weight !== null, "Weight is not preset");
    return this.weight;
  }

  getResistanceFromThickness(thickness) {
    AssertThrow(this.thickness === null, "Thickness is not user-defined");
    return thickness / this.conductivity;
  }

  getWeightFromThickness(thicknessInches) {
    AssertThrow(this.thickness === null, "Thickness is not user-defined");
    return this.density * (thicknessInches / 12.0);
  }
};

export class WallAndRoofData {
  static _instance = null;

  static instance() {
    if (!this._instance) {
      this._instance = new WallAndRoofData();
    }
    return this._instance;
  }

  constructor() {
    // NOTE - we load the old table too for backwards compatibility
    let oldCsvData = new CsvHelper(OLD_WallsAndRoofsDataCombined);
    this.oldWallDataMap = this._parseWallAndRoofData(oldCsvData);

    let newCsvData = new CsvHelper(WallsAndRoofsDataCombined);
    this.wallDataMap = this._parseWallAndRoofData(newCsvData);

    let absorptanceData = parseSimpleTable(AbsorptanceData);
    let materials = {};
    for (const row of absorptanceData) {
      let name = row['Outer Layer'];
      let absorptance = row['Absorptance'];
      materials[name] = {
        label: `${name} (${absorptance})`,
        value: Number(absorptance),
      }
    }
    this.MaterialAbsorptance = makeEnumWithDataAndLabels(materials);
  }

  _parseWallAndRoofData(csvData) {
    let defaults = [
      new WallMaterial({
        'Id': 'Outdoor_Surface_Resistance',
        'Material': 'Outdoor Surface Resistance',
        'Thickness': 0,
        'Conductivity': null,
        'Resistance': 0.25,
        'Density': null,
        'Weight': 0,
      }),
      new WallMaterial({
        'Id': 'Indoor_Layer_Air_Resistance',
        'Material': 'Indoor Layer Air Resistance',
        'Thickness': 0,
        'Conductivity': null,
        'Resistance': 0.68,
        'Density': null,
        'Weight': 0,
      })
    ];
    let wallMaterials = this._parseMaterialsTable(csvData, 'WallMaterials');
    let roofing = this._parseMaterialsTable(csvData, 'Roofing');
    let buildingBoardAndSiding = this._parseMaterialsTable(csvData, 'BuildingBoardAndSiding');
    let insulatingMaterials = this._parseMaterialsTable(csvData, 'Insulating');
    let masonryMaterials = this._parseMaterialsTable(csvData, 'Masonry');
    let plasteringMaterials = this._parseMaterialsTable(csvData, 'Plastering');
    let woods = this._parseMaterialsTable(csvData, 'Woods');
    return {
      'Wall Materials': wallMaterials,
      'Roofing': roofing,
      'Building Board and Siding': buildingBoardAndSiding,
      'Insulating Materials': insulatingMaterials,
      'Masonry Materials': masonryMaterials,
      'Plastering Materials': plasteringMaterials,
      'Woods': woods,
      'Defaults': defaults,
    }
  }

  _parseMaterialsTable(csvObj, tableName) {
    // TODO - fill in requiredCols and colInfo
    let tableData = csvObj.parseSimpleTable(tableName, {
      requiredCols: [],
      colInfo: {}
    });
    let materials = [];
    for (const row of tableData.rows) {
      let material = new WallMaterial(row);
      materials.push(material);
    }
    return materials;
  }

  getWallLayerCategories() {
    return Object.keys(this.wallDataMap);
  }

  getWallLayerTypes(categoryName, projectVersion) {
    if (!this.wallDataMap[categoryName]) {
      return [];
    }
    let materialNames = [];
    if (CompareProjectVersions(projectVersion, '1.4.1') < 0) {
      // For backwards compatibility, return different formats based on project version.
      // In the old way, we just used the material name string as the Field's select id.
      for (const row of this.oldWallDataMap[categoryName]) {
        materialNames.push(row.name);
      }
    } else {
      for (const row of this.wallDataMap[categoryName]) {
        materialNames.push({
          label: row.name,
          value: row.id,
        });
      }
    }
    return materialNames;
  }

  _convertToMaterialId(materialIdOrName, newTable, oldTable) {
    // For backwards compatibility, we allow lookup by either material ID or name.
    // We used to store the material type select by name (bad) then later switched to ID.
    // Steps:
    // First, see if it's an ID in the new data table.
    // If not, see if it's an ID in the old data table.
    // Last, check if it's a name in the old data table.
    let row = newTable.find((elem) => {
      return materialIdOrName === elem.id || materialIdOrName === elem.name;
    });
    if (row) {
      return row.id;
    }
    row = oldTable.find((elem) => {
      return materialIdOrName === elem.id || materialIdOrName === elem.name;
    });
    if (row) {
      return row.id;
    }
    throw new Error(`Could not find material with ID or name: ${materialIdOrName}`);
  }

  lookupWallMaterial(categoryName, materialIdOrName) {
    let table = this.wallDataMap[categoryName];
    if (!table) {
      return null;
    }
    let oldTable = this.oldWallDataMap[categoryName];
    let materialId = this._convertToMaterialId(materialIdOrName, table, oldTable);
    let res = table.find((elem) => {
      return elem.id == materialId;
    });
    return res;
  }
};

